// src/diagrams/routing/AdvancedEdgeComponents.js - Optimized with better caching and performance

import React, { memo, useMemo } from 'react';
import { getBezierPath, Position } from '@xyflow/react';
import { EdgeRoutingSystem } from './EdgeRoutingSystem';

let routingCache = new Map();
let lastCleanup = Date.now();
let edgeRenderCount = new Map();

// Defaults applied when a caller doesn't specify a parameter. These match the previous
// hardcoded singleton so behavior is unchanged when no routing config is supplied.
const ROUTING_SYSTEM_DEFAULTS = {
  gridSize: 15,
  nodePadding: 35,
  enableBundling: false,
  bundleThreshold: 0.7,
  crossingPenalty: 10,
  lengthPenalty: 1,
  turnPenalty: 2,
  proximityPenalty: 5,
  routingPolicy: 'AS_NEEDED'
};

// Routing systems keyed by their serialized config. The old code shared a single hardcoded
// singleton, so the user's Edge Routing Configuration preset never reached pathfinding. Now
// each distinct config (gridSize, nodePadding, penalties, policy, …) gets its own
// EdgeRoutingSystem, so changing the preset actually changes edge geometry. The map is bounded
// to avoid unbounded growth as configs vary.
const routingSystems = new Map();
const MAX_ROUTING_SYSTEMS = 24;

const getRoutingSystem = (options = {}) => {
  const config = { ...ROUTING_SYSTEM_DEFAULTS, ...options };
  const key = JSON.stringify(config);
  let system = routingSystems.get(key);
  if (!system) {
    system = new EdgeRoutingSystem(config);
    if (routingSystems.size >= MAX_ROUTING_SYSTEMS) {
      // Evict the oldest entry (insertion order) to bound memory.
      const oldestKey = routingSystems.keys().next().value;
      routingSystems.delete(oldestKey);
    }
    routingSystems.set(key, system);
  }
  return system;
};

// Edge tracking with cleanup
const edgesBySource = new Map();

// Performance monitoring with adaptive thresholds
const performanceMetrics = {
  renderCount: 0,
  lastPerformanceCheck: Date.now(),
  maxRenderCount: 100, // More aggressive limit for large graphs
  adaptiveThreshold: true
};

// Cache management
const CACHE_SIZE_LIMIT = 500;

const cleanupCache = () => {
  const now = Date.now();
  
  if (routingCache.size > CACHE_SIZE_LIMIT) {
    // Remove oldest entries
    const entries = Array.from(routingCache.entries());
    const toRemove = entries.slice(0, Math.floor(CACHE_SIZE_LIMIT / 2));
    toRemove.forEach(([key]) => routingCache.delete(key));
  }
  
  lastCleanup = now;
};

/**
 * Create smooth path from waypoints - optimized
 */
const createSmoothPathFromWaypoints = (waypoints) => {
  const cacheKey = `waypoints-${waypoints.map(p => `${p.x},${p.y}`).join('-')}`;
  
  if (routingCache.has(cacheKey)) {
    return routingCache.get(cacheKey);
  }
  
  if (waypoints.length < 2) return '';
  if (waypoints.length === 2) {
    const [start, end] = waypoints;
    const [path] = getBezierPath({
      sourceX: start.x,
      sourceY: start.y,
      targetX: end.x,
      targetY: end.y,
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top
    });
    routingCache.set(cacheKey, path);
    return path;
  }

  let pathString = `M${waypoints[0].x},${waypoints[0].y}`;
  
  // Simplified path generation for performance
  for (let i = 1; i < waypoints.length; i++) {
    pathString += ` L${waypoints[i].x},${waypoints[i].y}`;
  }
  
  routingCache.set(cacheKey, pathString);
  
  // Periodic cleanup
  if (Date.now() - lastCleanup > 30000) {
    cleanupCache();
  }
  
  return pathString;
};

/**
 * Create bundled path with optimization - simplified
 */
const createSmoothBundledPath = (sourceX, sourceY, targetX, targetY, offset, layoutDirection) => {
  const cacheKey = `bundled-${sourceX}-${sourceY}-${targetX}-${targetY}-${offset}-${layoutDirection}`;
  
  if (routingCache.has(cacheKey)) {
    return routingCache.get(cacheKey);
  }
  
  const isHorizontal = layoutDirection === 'LR';
  
  // Simple bezier for small offsets
  if (Math.abs(offset) < 5) {
    const [path] = getBezierPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      targetPosition: isHorizontal ? Position.Left : Position.Top
    });
    routingCache.set(cacheKey, path);
    return path;
  }
  
  // Simplified curved path for larger offsets
  const midX = (sourceX + targetX) / 2;
  const midY = (sourceY + targetY) / 2;
  
  const controlX = isHorizontal ? midX : midX + offset;
  const controlY = isHorizontal ? midY + offset : midY;
  
  const path = `M${sourceX},${sourceY} Q${controlX},${controlY} ${targetX},${targetY}`;
  
  routingCache.set(cacheKey, path);
  
  // Cleanup if needed
  if (Date.now() - lastCleanup > 30000) {
    cleanupCache();
  }
  
  return path;
};

/**
 * Enhanced curve generation for multiple edges - optimized
 */
const createMultiEdgePath = (sourceX, sourceY, targetX, targetY, edgeIndex, totalEdges, layoutDirection) => {
  const cacheKey = `multi-${sourceX}-${sourceY}-${targetX}-${targetY}-${edgeIndex}-${totalEdges}-${layoutDirection}`;
  
  if (routingCache.has(cacheKey)) {
    return routingCache.get(cacheKey);
  }
  
  if (totalEdges === 1) {
    const [path] = getBezierPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition: layoutDirection === 'LR' ? Position.Right : Position.Bottom,
      targetPosition: layoutDirection === 'LR' ? Position.Left : Position.Top
    });
    routingCache.set(cacheKey, path);
    return path;
  }
  
  const offsetAmount = (edgeIndex - (totalEdges - 1) / 2) * 20; // Reduced spacing
  const path = createSmoothBundledPath(sourceX, sourceY, targetX, targetY, offsetAmount, layoutDirection);
  
  routingCache.set(cacheKey, path);
  return path;
};

/**
 * Simplified routing for performance
 */
const performAdvancedRouting = (sourceX, sourceY, targetX, targetY, obstacles, layoutDirection, edgeId, routingConfig = {}) => {
  // Skip routing for edges that have been rendered many times
  const renderCount = edgeRenderCount.get(edgeId) || 0;
  edgeRenderCount.set(edgeId, renderCount + 1);

  if (renderCount > 10) {
    return null; // Use fallback path
  }

  if (!obstacles || obstacles.length === 0) {
    return null;
  }

  // Build the routing system for the *selected* config so gridSize/nodePadding/penalties/policy
  // actually take effect. The resolved config is folded into the cache key so switching presets
  // doesn't reuse a stale path computed under a different config.
  const routingSystem = getRoutingSystem(routingConfig);
  const config = routingSystem.config;
  const policy = config.routingPolicy || 'SMART';
  const configKey = `${config.gridSize}-${config.nodePadding}-${config.optimizationIterations}-${policy}`;
  const cacheKey = `route-${sourceX}-${sourceY}-${targetX}-${targetY}-${obstacles.length}-${configKey}`;

  if (routingCache.has(cacheKey)) {
    return routingCache.get(cacheKey);
  }

  const sourcePoint = { x: sourceX, y: sourceY };
  const targetPoint = { x: targetX, y: targetY };

  // Quick check if routing is needed
  const distance = Math.sqrt(Math.pow(targetX - sourceX, 2) + Math.pow(targetY - sourceY, 2));
  if (distance < 100) {
    return null; // Too short to need routing
  }

  // Only check first few obstacles for performance
  const relevantObstacles = obstacles.slice(0, Math.min(10, obstacles.length));

  // routingPolicy controls *when* we route:
  //  ALWAYS    – always compute an obstacle-aware path (highest quality, used by QUALITY)
  //  AS_NEEDED – only when the straight line actually crosses an obstacle (PERFORMANCE/LARGE_GRAPH)
  //  SMART     – intersection-driven, like AS_NEEDED (BALANCED)
  const intersects = routingSystem.pathfinder.checkPathIntersectsObstacles(
    sourcePoint,
    targetPoint,
    relevantObstacles
  );
  const needsRouting = policy === 'ALWAYS' ? true : intersects;

  if (needsRouting) {
    try {
      const path = routingSystem.pathfinder.findPath(
        sourcePoint,
        targetPoint,
        relevantObstacles
      );

      if (path && path.length > 2) {
        // For higher-quality policies, refine the raw A* path with the penalty optimizer
        // (honors the config's optimizationIterations + turn/length penalties) so QUALITY/BALANCED
        // visibly differ from the unoptimized PERFORMANCE/LARGE_GRAPH paths.
        const finalPath = (policy === 'ALWAYS' || policy === 'SMART')
          ? routingSystem.penaltySystem.optimizePath(path, relevantObstacles, [])
          : path;
        const smoothPath = createSmoothPathFromWaypoints(finalPath);
        routingCache.set(cacheKey, smoothPath);
        return smoothPath;
      }
    } catch (error) {
      console.error('Pathfinding error:', error);
    }
  }

  return null;
};

/**
 * Optimized Advanced Edge Component
 */
export const AdvancedEdge = memo(({ 
  id, 
  sourceX, 
  sourceY, 
  targetX, 
  targetY, 
  style, 
  markerEnd, 
  label, 
  data 
}) => {
  const layoutDirection = data?.layoutDirection || 'TB';
  // Stable signature of the selected routing config so the path recomputes when the user
  // changes the Edge Routing Configuration preset (esp. CUSTOM changes, which don't remount).
  const routingConfigKey = useMemo(
    () => (data?.routingConfig ? JSON.stringify(data.routingConfig) : ''),
    [data?.routingConfig]
  );

  const edgePath = useMemo(() => {
    performanceMetrics.renderCount++;

    const obstacles = data?.obstacles || [];

    // Adaptive performance threshold
    if (performanceMetrics.adaptiveThreshold && obstacles.length > 20) {
      performanceMetrics.maxRenderCount = 50;
    }

    const sourceNodeId = id.split('-')[0];
    const handleId = data?.actualHandle || 'default';
    const edgeKey = `${sourceNodeId}-${handleId}`;

    // Track this edge for fan-out calculations
    if (!edgesBySource.has(edgeKey)) {
      edgesBySource.set(edgeKey, new Set());
    }
    edgesBySource.get(edgeKey).add(id);

    // Calculate offset based on edge index
    const edgesFromSameHandle = Array.from(edgesBySource.get(edgeKey));
    const edgeIndex = edgesFromSameHandle.indexOf(id);
    const totalEdges = edgesFromSameHandle.length;

    const dx = targetX - sourceX;
    const dy = targetY - sourceY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Performance optimization for large graphs
    const shouldSkipRouting =
      performanceMetrics.renderCount > performanceMetrics.maxRenderCount ||
      obstacles.length > 30 ||
      distance < 50;

    // Try advanced routing only if needed
    if (!shouldSkipRouting && obstacles.length > 0 && distance > 100) {
      const routedPath = performAdvancedRouting(sourceX, sourceY, targetX, targetY, obstacles, layoutDirection, id, data?.routingConfig);
      if (routedPath) {
        return routedPath;
      }
    }

    // Fallback to multi-edge path
    return createMultiEdgePath(sourceX, sourceY, targetX, targetY, edgeIndex, totalEdges, layoutDirection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, sourceX, sourceY, targetX, targetY, layoutDirection, data?.actualHandle, data?.obstacles, routingConfigKey]);

  const labelPos = useMemo(() => calculateLabelPosition([
    { x: sourceX, y: sourceY },
    { x: targetX, y: targetY }
  ]), [sourceX, sourceY, targetX, targetY]);
  
  return (
    <g>
      <path
        id={id}
        d={edgePath}
        style={{
          fill: 'none',
          strokeWidth: 2,
          stroke: '#666',
          ...style
        }}
        markerEnd={markerEnd}
      />
      {label && (
        <text
          x={labelPos.x}
          y={labelPos.y}
          textAnchor="middle"
          fontSize="10"
          fill="#333"
          dy="-5"
        >
          {label}
        </text>
      )}
    </g>
  );
}, (prevProps, nextProps) => {
  // Enhanced comparison to prevent unnecessary re-renders
  return (
    prevProps.id === nextProps.id &&
    Math.abs(prevProps.sourceX - nextProps.sourceX) < 1 &&
    Math.abs(prevProps.sourceY - nextProps.sourceY) < 1 &&
    Math.abs(prevProps.targetX - nextProps.targetX) < 1 &&
    Math.abs(prevProps.targetY - nextProps.targetY) < 1 &&
    prevProps.data?.obstacles?.length === nextProps.data?.obstacles?.length &&
    prevProps.data?.layoutDirection === nextProps.data?.layoutDirection &&
    prevProps.data?.actualHandle === nextProps.data?.actualHandle &&
    // Re-render when the selected routing config changes so preset switches take effect.
    prevProps.data?.routingConfig === nextProps.data?.routingConfig
  );
});

/**
 * Optimized Error Edge
 */
export const AdvancedErrorEdge = memo(({ 
  id, 
  sourceX, 
  sourceY, 
  targetX, 
  targetY, 
  style, 
  markerEnd, 
  label, 
  data 
}) => {
  const layoutDirection = data?.layoutDirection || 'TB';

  const edgePath = useMemo(() => {
    const sourceNodeId = id.split('-')[0];
    const handleId = data?.actualHandle || 'default';
    const edgeKey = `${sourceNodeId}-${handleId}`;

    if (!edgesBySource.has(edgeKey)) {
      edgesBySource.set(edgeKey, new Set());
    }
    edgesBySource.get(edgeKey).add(id);

    const edgesFromSameHandle = Array.from(edgesBySource.get(edgeKey));
    const edgeIndex = edgesFromSameHandle.indexOf(id);
    const totalEdges = edgesFromSameHandle.length;

    // Simplified routing for error edges
    return createMultiEdgePath(sourceX, sourceY, targetX, targetY, edgeIndex, totalEdges, layoutDirection);
  }, [id, sourceX, sourceY, targetX, targetY, layoutDirection, data?.actualHandle]);

  const labelPos = useMemo(() => calculateLabelPosition([
    { x: sourceX, y: sourceY },
    { x: targetX, y: targetY }
  ]), [sourceX, sourceY, targetX, targetY]);
  
  return (
    <g>
      <path
        id={id}
        d={edgePath}
        style={{
          fill: 'none',
          strokeWidth: 2,
          stroke: '#f44336',
          strokeDasharray: '5,5',
          ...style
        }}
        markerEnd={markerEnd}
      />
      {(label || data?.label) && (
        <text
          x={labelPos.x}
          y={labelPos.y}
          textAnchor="middle"
          fontSize="10"
          fill="#f44336"
          dy="-5"
        >
          {label || data.label}
        </text>
      )}
    </g>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    Math.abs(prevProps.sourceX - nextProps.sourceX) < 1 &&
    Math.abs(prevProps.sourceY - nextProps.sourceY) < 1 &&
    Math.abs(prevProps.targetX - nextProps.targetX) < 1 &&
    Math.abs(prevProps.targetY - nextProps.targetY) < 1 &&
    prevProps.data?.layoutDirection === nextProps.data?.layoutDirection
  );
});

/**
 * Optimized Condition Edge
 */
export const AdvancedConditionEdge = memo(({ 
  id, 
  sourceX, 
  sourceY, 
  targetX, 
  targetY, 
  style, 
  markerEnd, 
  label, 
  data 
}) => {
  const layoutDirection = data?.layoutDirection || 'TB';

  const edgePath = useMemo(() => {
    const sourceNodeId = id.split('-')[0];
    const handleId = data?.actualHandle || 'default';
    const edgeKey = `${sourceNodeId}-${handleId}`;

    if (!edgesBySource.has(edgeKey)) {
      edgesBySource.set(edgeKey, new Set());
    }
    edgesBySource.get(edgeKey).add(id);

    const edgesFromSameHandle = Array.from(edgesBySource.get(edgeKey));
    const edgeIndex = edgesFromSameHandle.indexOf(id);
    const totalEdges = edgesFromSameHandle.length;

    // Simplified routing for condition edges
    return createMultiEdgePath(sourceX, sourceY, targetX, targetY, edgeIndex, totalEdges, layoutDirection);
  }, [id, sourceX, sourceY, targetX, targetY, layoutDirection, data?.actualHandle]);

  const labelPos = useMemo(() => calculateLabelPosition([
    { x: sourceX, y: sourceY },
    { x: targetX, y: targetY }
  ]), [sourceX, sourceY, targetX, targetY]);
  
  return (
    <g>
      <path
        id={id}
        d={edgePath}
        style={{
          fill: 'none',
          strokeWidth: 2,
          stroke: '#2196f3',
          ...style
        }}
        markerEnd={markerEnd}
      />
      {(label || data?.label) && (
        <text
          x={labelPos.x}
          y={labelPos.y}
          textAnchor="middle"
          fontSize="10"
          fill="#2196f3"
          dy="-5"
          style={{ fontWeight: 'bold' }}
        >
          {label || data.label}
        </text>
      )}
    </g>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    Math.abs(prevProps.sourceX - nextProps.sourceX) < 1 &&
    Math.abs(prevProps.sourceY - nextProps.sourceY) < 1 &&
    Math.abs(prevProps.targetX - nextProps.targetX) < 1 &&
    Math.abs(prevProps.targetY - nextProps.targetY) < 1 &&
    prevProps.data?.layoutDirection === nextProps.data?.layoutDirection
  );
});

/**
 * Bundled edge component - simplified
 */
export const BundledEdge = memo(({ 
  id, 
  sourceX, 
  sourceY, 
  targetX, 
  targetY, 
  style, 
  markerEnd, 
  data, 
  label 
}) => {
  const bundleOffset = data?.bundleOffset || 0;
  const layoutDirection = data?.layoutDirection || 'TB';
  
  const edgePath = useMemo(() => {
    if (data?.routedPath && data.routedPath.length > 1) {
      return createSmoothPathFromWaypoints(data.routedPath);
    }
    
    return createSmoothBundledPath(sourceX, sourceY, targetX, targetY, bundleOffset, layoutDirection);
  }, [sourceX, sourceY, targetX, targetY, bundleOffset, layoutDirection, data?.routedPath]);
  
  const labelPos = useMemo(() => calculateLabelPosition(data?.routedPath || [
    { x: sourceX, y: sourceY },
    { x: targetX, y: targetY }
  ]), [sourceX, sourceY, targetX, targetY, data?.routedPath]);
  
  return (
    <g>
      <path
        id={id}
        d={edgePath}
        style={{
          fill: 'none',
          strokeWidth: 2,
          stroke: '#666',
          opacity: 0.8,
          ...style
        }}
        markerEnd={markerEnd}
      />
      {label && (
        <text
          x={labelPos.x}
          y={labelPos.y}
          textAnchor="middle"
          fontSize="10"
          fill="#333"
          dy="-5"
        >
          {label}
        </text>
      )}
    </g>
  );
});

// Utility functions
function calculateLabelPosition(path) {
  if (path.length === 0) return { x: 0, y: 0 };
  if (path.length === 1) return path[0];
  
  const midIndex = Math.floor(path.length / 2);
  
  if (path.length % 2 === 1) {
    return path[midIndex];
  } else {
    const p1 = path[midIndex - 1];
    const p2 = path[midIndex];
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2
    };
  }
}

// Export edge types for React Flow
export const advancedEdgeTypes = {
  advancedEdge: AdvancedEdge,
  advancedErrorEdge: AdvancedErrorEdge,
  advancedConditionEdge: AdvancedConditionEdge,
  bundledEdge: BundledEdge
};

// Enhanced cleanup function
export const cleanupEdgeTracking = () => {
  edgesBySource.clear();
  routingCache.clear();
  edgeRenderCount.clear();
  performanceMetrics.renderCount = 0;
  performanceMetrics.lastPerformanceCheck = Date.now();
  performanceMetrics.maxRenderCount = 100;
  lastCleanup = Date.now();
};

// Export performance monitoring
export const getPerformanceMetrics = () => ({ ...performanceMetrics });

// Export routing system getter for external configuration
export { getRoutingSystem };